import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import digitalprocess from "../assets/our digital process.png";
import ecommerce from "../assets/Ecommerce.png";
import application from "../assets/application.png";
import brand from "../assets/brand.jpg";
import dm from "../assets/dm.jpg";
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

export default function CompetitionPortfolio() {
  const [activeSection, setActiveSection] = useState("hero");
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const sectionRefs = useRef([]);
  const magneticRefs = useRef([]);
  const blobRef = useRef(null);
  const serviceCardRefs = useRef([]);

  // Services data
  const services = [
    {
      title: "WEB DESIGNING",
      icon: "🌐",
      description:
        "Pixel-perfect, responsive designs with cutting-edge animations",
      color: "#6693F1",
    },
    {
      title: "WEB APPLICATION",
      icon: "🚀",
      description: "High-performance apps with modern frameworks",
      color: "#FF6B6B",
    },
    {
      title: "DIGITAL MARKETING",
      icon: "📈",
      description: "Data-driven campaigns for maximum ROI",
      color: "#4ECDC4",
    },
    {
      title: "SOCIAL MEDIA HANDLING",
      icon: "🤳",
      description: "Viral content strategies across platforms",
      color: "#FFBE0B",
    },
    {
      title: "BRAND DESIGNING",
      icon: "🎨",
      description: "Complete visual identity systems",
      color: "#9D4EDD",
    },
  ];

  // Portfolio items
  const portfolioItems = [
    {
      title: "Clothes E-Commerce",
      category: "Web Design",
      year: "2025",
      image: ecommerce,
    },
    {
      title: "Finanace Application",
      category: "Mobile Application",
      year: "2025",
      image: application,
    },
    {
      title: "Metaverse Branding",
      category: "Brand Design",
      year: "2025",
      image: brand,
    },
    {
      title: "MGR Career Consultancy",
      category: "Digital Marketing",
      year: "2025",
      image: dm,
    },
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Sarah K.",
      company: "TechNova",
      quote: "Redefined our digital presence completely",
      image: "/avatar1.jpg",
    },
    {
      name: "Arjun P.",
      company: "DesignHub",
      quote: "Most innovative team we've worked with",
      image: "/avatar2.jpg",
    },
    {
      name: "Emma L.",
      company: "MarketGrow",
      quote: "Tripled our engagement in 3 months",
      image: "/avatar3.jpg",
    },
  ];

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Close menu when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Track mouse position for cursor effects
    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      if (blobRef.current) {
        gsap.to(blobRef.current, {
          x: e.clientX - 50,
          y: e.clientY - 50,
          duration: 0.8,
          ease: "power3.out",
        });
      }
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Magnetic button effect
    magneticRefs.current.forEach((el) => {
      if (!el) return;

      el.addEventListener("mousemove", (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        gsap.to(el, {
          x: (x - rect.width / 2) * 0.3,
          y: (y - rect.height / 2) * 0.3,
          duration: 0.5,
          ease: "power3.out",
        });
      });

      el.addEventListener("mouseleave", () => {
        gsap.to(el, {
          x: 0,
          y: 0,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)",
        });
      });
    });

    // Section scroll triggers
    sectionRefs.current.forEach((section) => {
      if (section) {
        ScrollTrigger.create({
          trigger: section,
          start: "top center",
          end: "bottom center",
          onEnter: () => setActiveSection(section.id),
          onEnterBack: () => setActiveSection(section.id),
        });
      }
    });

    // Hero animation
    const elements = heroRef.current.querySelectorAll(".hero-element");

    gsap.set(elements, { opacity: 0, y: 50 }); // Ensure it always starts fresh

    gsap.to(elements, {
      y: 0,
      opacity: 1,
      stagger: 0.2,
      duration: 1.5,
      ease: "power3.out",
    });

    // Floating blob animation
    gsap.to(blobRef.current, {
      motionPath: {
        path: [
          { x: 0, y: 0 },
          { x: 100, y: -50 },
          { x: 50, y: -100 },
          { x: -50, y: -50 },
          { x: 0, y: 0 },
        ],
        curviness: 1.5,
      },
      duration: 20,
      repeat: -1,
      ease: "none",
    });

    // Init floating particles
    initParticles();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const initParticles = () => {
    // Advanced particle system would be implemented here
    // Using canvas or WebGL for performance
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative bg-[#051C50] text-[#F7FEFF] overflow-hidden">
      {/* Animated blob background */}
      <div
        ref={blobRef}
        className="fixed w-[300px] h-[300px] rounded-full bg-[#6693F1] opacity-10 blur-[80px] pointer-events-none z-0"
        style={{
          filter: "blur(80px)",
        }}
      />

      {/* Custom cursor */}
      <div
        className="fixed w-8 h-8 rounded-full border-2 border-[#6693F1] pointer-events-none z-50 mix-blend-difference transition-transform duration-300 ease-out"
        style={{
          transform: `translate(${cursorPos.x - 14}px, ${
            cursorPos.y - 14
          }px) scale(${hoveredItem ? 2 : 1})`,
          backgroundColor: hoveredItem ? hoveredItem.color : "transparent",
          borderColor: hoveredItem ? hoveredItem.color : "#6693F1",
        }}
      />

      {/* Navigation */}
   <nav className="fixed w-full z-40 py-6 px-8 flex justify-between items-center backdrop-blur-2xl bg-gradient-to-r from-[#051C50]/40 via-[#0A1A3A]/40 to-[#051C50]/40 border-b border-white/20 shadow-2xl">
      <div
        className="text-2xl font-bold"
        onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <span className="text-[#6693F1]">TECK</span>ANTS
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex space-x-8">
        {["home", "services", "work", "testimonials", "contact"].map(
          (item) => (
            <button
              key={item}
              onClick={() => {
                scrollToSection(item);
                setIsMenuOpen(false);
              }}
              className={`relative capitalize px-2 py-1 overflow-hidden ${
                activeSection === item ? "text-[#6693F1]" : "text-[#F7FEFF]"
              }`}
              onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span className="relative z-10">{item}</span>
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#6693F1] transition-all duration-500 ${
                  activeSection === item ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </button>
          ),
        )}
      </div>

      {/* Desktop CTA Button */}
      <button
        ref={(el) => (magneticRefs.current[0] = el)}
        className="hidden md:block relative overflow-hidden px-6 py-2 rounded-full bg-[#6693F1] text-[#051C50] font-bold group"
        onMouseEnter={() => setHoveredItem({ color: "#051C50" })}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <span className="relative z-10">Get in Touch</span>
        <span className="absolute inset-0 bg-[#F7FEFF] scale-y-0 group-hover:scale-y-100 origin-bottom transition-transform duration-300 ease-in-out" />
      </button>

      {/* Mobile Hamburger Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="md:hidden relative w-10 h-10 flex flex-col justify-center items-center gap-1.5 group z-50"
        aria-label="Toggle menu"
      >
        <span className={`w-6 h-0.5 bg-[#F7FEFF] transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'rotate-45 translate-y-2' : ''
        }`} />
        <span className={`w-6 h-0.5 bg-[#F7FEFF] transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'opacity-0' : ''
        }`} />
        <span className={`w-6 h-0.5 bg-[#F7FEFF] transition-all duration-300 ease-in-out ${
          isMenuOpen ? '-rotate-45 -translate-y-2' : ''
        }`} />
      </button>
    </nav>

    {/* Mobile Menu Overlay */}
    <div className={`fixed inset-0 z-30 md:hidden transition-all duration-500 ${
      isMenuOpen ? 'visible' : 'invisible pointer-events-none'
    }`}>
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 backdrop-blur-xl bg-[#051C50]/90 transition-opacity duration-500 ${
          isMenuOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />
      
      {/* Menu Content */}
      <div className={`absolute right-0 top-0 h-full w-80 bg-gradient-to-b from-[#051C50]/95 via-[#0A1A3A]/95 to-[#051C50]/95 backdrop-blur-2xl border-l border-white/20 shadow-2xl transform transition-transform duration-500 ease-out ${
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col pt-24 px-8 gap-6">
          {/* Mobile Navigation Items */}
          {["home", "services", "work", "testimonials", "contact"].map(
            (item, index) => (
              <button
                key={item}
                onClick={() => {
                  scrollToSection(item);
                  setIsMenuOpen(false);
                }}
                className={`relative group text-left py-3 text-2xl font-medium transition-all duration-300 transform hover:translate-x-2 ${
                  activeSection === item ? "text-[#6693F1]" : "text-[#F7FEFF]"
                }`}
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: isMenuOpen ? 'slideIn 0.5s ease-out forwards' : 'none',
                  opacity: 0,
                }}
                onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="relative capitalize">
                  {item}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#6693F1] transform origin-left transition-transform duration-300 ${
                    activeSection === item ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`} />
                </span>
              </button>
            ),
          )}
          
          {/* Divider */}
          <div className="my-4 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          {/* Mobile CTA Button */}
          <button
            onClick={() => {
              // Add your contact function here
              setIsMenuOpen(false);
            }}
            className="relative group mt-4 px-8 py-4 rounded-full bg-gradient-to-r from-[#6693F1] to-[#4ECDC4] text-[#051C50] font-bold text-lg overflow-hidden"
            onMouseEnter={() => setHoveredItem({ color: "#051C50" })}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              Get in Touch
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <span className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
          </button>

          {/* Social Links (Optional) */}
          <div className="absolute bottom-8 left-0 right-0 px-8">
            <div className="flex justify-center gap-6 pt-8 border-t border-white/10">
              {['Twitter', 'LinkedIn', 'Instagram'].map((social) => (
                <a
                  key={social}
                  href="#"
                  className="text-[#F7FEFF]/60 hover:text-[#6693F1] transition-colors text-sm"
                >
                  {social}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Slide-in animation keyframes */}
    <style jsx>{`
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `}</style>

      {/* Hero Section */}
      <section
        id="hero"
        ref={(el) => {
          heroRef.current = el;
          sectionRefs.current[0] = el;
        }}
        className="min-h-screen flex items-center justify-center px-8 relative z-10"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="hero-element text-6xl md:text-8xl font-bold mb-8 leading-tight">
            <span className="text-[#F7FEFF]">We Create</span>{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-[#6693F1] to-[#9D4EDD]">
                Digital
              </span>
              <span className="absolute bottom-0 left-0 w-full h-3 bg-[#6693F1] opacity-30 -z-1" />
            </span>{" "}
            <span className="text-[#F7FEFF]">Experiences</span>
          </h1>

          <p className="hero-element text-xl md:text-2xl max-w-3xl mx-auto mb-12 opacity-90">
            Award-winning digital agency specializing in innovative web
            solutions that push creative boundaries.
          </p>

          <div className="hero-element flex flex-col sm:flex-row justify-center gap-6">
            <button
              ref={(el) => (magneticRefs.current[1] = el)}
              onClick={() => scrollToSection("work")}
              className="relative overflow-hidden px-8 py-4 rounded-full bg-[#6693F1] text-[#051C50] font-bold group"
              onMouseEnter={() => setHoveredItem({ color: "#051C50" })}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span className="relative z-10">View Our Work</span>
              <span className="absolute inset-0 bg-[#F7FEFF] scale-y-0 group-hover:scale-y-100 origin-bottom transition-transform duration-300 ease-in-out" />
            </button>

            <button
              onClick={() => scrollToSection("services")}
              className="px-8 py-4 rounded-full border-2 border-[#6693F1] text-[#F7FEFF] font-bold hover:bg-[#6693F1]/20 transition-all"
              onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
              onMouseLeave={() => setHoveredItem(null)}
            >
              Our Services
            </button>
          </div>
        </div>

        {/* Animated scroll indicator */}
        <div
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce-slow"
          onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg
            className="w-8 h-8 text-[#6693F1]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 13L12 18L17 13M7 6L12 11L17 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </section>

      {/* About Us - Aligned with Your Visual Identity */}
      <section
        id="about"
        ref={(el) => (sectionRefs.current[1] = el)}
        className="relative py-24 px-4 sm:px-8 overflow-hidden"
      >
        {/* Background matching your style */}
        <div className="absolute inset-0 bg-[#051C50]/10 backdrop-blur-sm z-0">
          <div className="absolute -top-1/3 -right-1/4 w-full h-full opacity-20">
            <div
              className="absolute w-[300px] h-[300px] rounded-full bg-[#6693F1] blur-[100px]"
              style={{
                transform: "translate(30%, -20%)",
              }}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              <span
                className="text-[#F7FEFF] block hover:text-[#6693F1] transition-colors duration-300 cursor-default"
                onMouseEnter={() => setHoveredItem({ color: "#F7FEFF" })}
                onMouseLeave={() => setHoveredItem(null)}
              >
                Who We
              </span>
              <span
                className="text-[#6693F1] hover:text-[#F7FEFF] transition-colors duration-300 cursor-default"
                onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                onMouseLeave={() => setHoveredItem(null)}
              >
                Are
              </span>
            </h2>

            <div className="space-y-6">
              <p className="text-lg md:text-xl text-[#F7FEFF]/90 leading-relaxed">
                We{" "}
                <span
                  className="font-semibold text-[#6693F1] hover:text-[#F7FEFF] cursor-default transition-colors"
                  onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  transform visions
                </span>{" "}
                into digital experiences that captivate and convert. Our process
                blends strategic thinking with bleeding-edge execution.
              </p>

              {/* Interactive Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { value: "5+", label: "Years", color: "#6693F1" },
                  { value: "50+", label: "Projects", color: "#F7FEFF" },
                  { value: "100%", label: "Satisfaction", color: "#6693F1" },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="p-4 bg-[#0A2A7A]/20 rounded-lg border border-[#6693F1]/20 hover:bg-[#6693F1]/10 transition-all duration-300 cursor-default"
                    onMouseEnter={() => setHoveredItem({ color: stat.color })}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <p
                      className="text-2xl font-bold"
                      style={{ color: stat.color }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-sm opacity-80">{stat.label}</p>
                  </div>
                ))}
              </div>

              <p className="text-lg text-[#F7FEFF]/80 leading-relaxed">
                Since our founding in 2020, we've become the{" "}
                <span
                  className="font-medium text-[#6693F1] hover:text-[#F7FEFF] cursor-default transition-colors"
                  onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  secret weapon
                </span>{" "}
                for brands that refuse to settle for ordinary digital solutions.
              </p>
            </div>
          </div>

          {/* Visual Element with your aesthetic */}
          <div className="relative h-full min-h-[400px] group">
            {/* Main visual with interactive effects */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl border border-[#6693F1]/30">
              <img
                src={digitalprocess}
                alt="Our digital process"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#051C50]/70 via-transparent to-[#6693F1]/20"></div>
            </div>

            {/* Floating elements matching your blob style */}
            <div
              className="absolute -bottom-8 -left-8 w-40 h-40 bg-[#6693F1]/10 rounded-full blur-[40px] group-hover:blur-[60px] transition-all duration-500"
              onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
              onMouseLeave={() => setHoveredItem(null)}
            />
            <div
              className="absolute -top-10 -right-10 w-32 h-32 bg-[#F7FEFF]/05 rounded-full blur-[30px] group-hover:blur-[50px] transition-all duration-500"
              onMouseEnter={() => setHoveredItem({ color: "#F7FEFF" })}
              onMouseLeave={() => setHoveredItem(null)}
            />
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section
        id="services"
        ref={(el) => (sectionRefs.current[1] = el)}
        className="py-32 px-8 relative z-10 min-h-screen flex items-center"
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Sticky info */}
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="space-y-4">
                <div className="w-20 h-px bg-gradient-to-r from-[#6693F1] to-transparent" />
                <h2 className="text-5xl md:text-6xl font-bold">
                  <span className="text-white">What we</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#6693F1] to-[#9D4EDD] bg-clip-text text-transparent">
                    Deliver
                  </span>
                </h2>
                <p className="text-white/60 text-lg leading-relaxed">
                  Transforming ideas into exceptional digital experiences that
                  drive growth and engagement.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-[#6693F1]">50+</div>
                  <div className="text-sm text-white/50">
                    Projects Completed
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#4ECDC4]">98%</div>
                  <div className="text-sm text-white/50">
                    Client Satisfaction
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Services List */}
            <div className="space-y-6">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group relative bg-white/5 hover:bg-white/10 rounded-2xl p-6 transition-all duration-500 cursor-pointer border border-white/10 hover:border-[${service.color}]/50"
                  onMouseEnter={() => setHoveredItem(service)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div className="flex items-start gap-6">
                    {/* Animated icon */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-[${service.color}] blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 rounded-full" />
                      <div className="relative text-5xl group-hover:scale-110 transition-transform duration-300">
                        {service.icon}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-2xl font-bold text-white">
                          {service.title}
                        </h3>
                        {/* Number badge */}
                        <div className="text-4xl font-bold text-white/10 group-hover:text-[${service.color}]/20 transition-colors">
                          {(index + 1).toString().padStart(2, "0")}
                        </div>
                      </div>
                      <p className="text-white/60 group-hover:text-white/80 transition-colors mb-4">
                        {service.description}
                      </p>

                      {/* Expandable content */}
                      <div className="max-h-0 group-hover:max-h-20 overflow-hidden transition-all duration-500">
                        <div className="flex gap-4">
                          <button className="text-sm text-[${service.color}] hover:underline">
                            View case studies →
                          </button>
                          <button className="text-sm text-white/50 hover:text-white/70">
                            Get quote →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Animated progress bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 rounded-b-2xl overflow-hidden">
                    <div
                      className="h-full w-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700"
                      style={{ backgroundColor: service.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio/Work Section */}
      {/* Unique Portfolio Section */}
      <section
        id="work"
        ref={(el) => (sectionRefs.current[2] = el)}
        className="py-32 px-4 sm:px-8 relative z-10 bg-[#051C50]/20 overflow-hidden"
      >
        {/* Background elements */}
        <div className="absolute -top-1/3 left-0 w-[120%] h-[150%] bg-[#6693F1]/5 blur-3xl -rotate-12 z-0"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Animated title */}
          <div className="mb-20 text-center overflow-hidden">
            <h2 className="text-4xl md:text-6xl font-bold inline-block">
              <span className="text-[#F7FEFF] block md:inline-block md:mr-4">
                Selected
              </span>
              <span className="text-[#6693F1] relative">
                Works
                <span className="absolute bottom-0 left-0 w-full h-1 bg-[#6693F1] transform scale-x-0 origin-left transition-transform duration-500"></span>
              </span>
            </h2>
          </div>

          {/* Diagonal grid layout */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 transform rotate-0 md:-rotate-1 origin-center">
              {portfolioItems.map((item, index) => (
                <div
                  key={index}
                  className={`group relative h-[80vh] min-h-[500px] overflow-hidden rounded-3xl border border-[#6693F1]/20 ${
                    index % 2 === 0 ? "md:-translate-y-10" : "md:translate-y-10"
                  }`}
                  onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {/* Project image with parallax effect */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div
                      className="w-full h-full scale-110 group-hover:scale-100 transition-transform duration-700"
                      style={{
                        backgroundImage: `linear-gradient(
                          to top,
                          rgba(5, 28, 80, 0.95),
                          rgba(5, 28, 80, 0.35)
                        ), url(${item?.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    ></div>
                  </div>

                  {/* Content overlay */}
                  <div className="absolute bottom-0 left-0 w-full p-8 bg-gradient-to-t from-[#051C50] to-transparent">
                    <div className="transform translate-y-10 group-hover:translate-y-0 transition-transform duration-500">
                      <span className="text-[#6693F1] text-sm font-mono">
                        {item?.year}
                      </span>
                      <h3 className="text-3xl md:text-4xl font-bold mb-2 text-[#F7FEFF]">
                        {item?.title}
                      </h3>
                      <p className="text-[#F7FEFF]/80 mb-4">{item.category}</p>
                    </div>

                    {/* Hidden details that slide up */}
                    <div className="opacity-0 group-hover:opacity-100 transform translate-y-5 group-hover:translate-y-0 transition-all duration-500 delay-100">
                      {/* <p className="text-[#F7FEFF]/90 mb-6">
                        {item.description}
                      </p> */}
                      <button className="px-6 py-2 rounded-full border border-[#6693F1] text-[#F7FEFF] hover:bg-[#6693F1]/30 transition-all flex items-center gap-2">
                        View Project
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-[#6693F1] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              ))}
            </div>
          </div>

          {/* View all button with magnetic effect */}
          <div className="text-center mt-32">
            <div className="inline-block transform hover:-translate-y-2 transition-transform duration-300">
              <button
                ref={(el) => (magneticRefs.current[2] = el)}
                className="relative overflow-hidden px-8 py-4 rounded-full bg-transparent border-2 border-[#6693F1] text-[#F7FEFF] font-bold group"
                onMouseEnter={() => setHoveredItem({ color: "#F7FEFF" })}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Explore Full Portfolio
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    ></path>
                  </svg>
                </span>
                <span className="absolute inset-0 bg-[#6693F1] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="testimonials"
        ref={(el) => (sectionRefs.current[3] = el)}
        className="py-32 px-8 relative z-10"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold mb-4 text-center">
            <span className="text-[#F7FEFF]">Client</span>{" "}
            <span className="text-[#6693F1]">Testimonials</span>
          </h2>
          <p className="text-xl text-center max-w-3xl mx-auto mb-20 opacity-90">
            What industry leaders say about our work
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="relative rounded-2xl p-8 bg-[#051C50]/50 backdrop-blur-md border border-[#6693F1]/20 hover:border-[#6693F1]/50 transition-all"
                onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div className="text-5xl text-[#6693F1] mb-6">"</div>
                <p className="text-xl mb-8 italic">{testimonial.quote}</p>
                <div className="flex items-center">
                  <div className="w-16 h-16 rounded-full bg-[#6693F1]/10 mr-4 flex items-center justify-center overflow-hidden">
                    {/* Client image would go here */}
                    <span className="text-2xl">👤</span>
                  </div>
                  <div>
                    <div className="font-bold text-lg">{testimonial.name}</div>
                    <div className="opacity-80">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        ref={(el) => (sectionRefs.current[4] = el)}
        className="py-32 px-8 relative z-10"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-6xl font-bold mb-8">
                <span className="text-[#F7FEFF]">Let's</span>{" "}
                <span className="text-[#6693F1]">Connect</span>
              </h2>
              <p className="text-xl max-w-md mb-12 opacity-90">
                Ready to start your next project? Get in touch with our team
                today.
              </p>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="text-[#6693F1] mr-6 mt-1">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Email Us</h3>
                    <p className="text-lg opacity-90">teckants@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="text-[#6693F1] mr-6 mt-1">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Social Media</h3>
                    <div className="flex space-x-4">
                      {["Twitter", "Dribbble", "LinkedIn", "Instagram"].map(
                        (social) => (
                          <a
                            key={social}
                            href="#"
                            className="opacity-80 hover:opacity-100 hover:text-[#6693F1] transition-all"
                            onMouseEnter={() =>
                              setHoveredItem({ color: "#6693F1" })
                            }
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            {social}
                          </a>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#051C50]/50 backdrop-blur-md rounded-2xl p-8 border border-[#6693F1]/20">
              <h3 className="text-2xl font-bold mb-8">Send us a message</h3>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full bg-[#051C50]/50 border border-[#6693F1]/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6693F1]"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="w-full bg-[#051C50]/50 border border-[#6693F1]/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6693F1]"
                      placeholder="Your email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    className="w-full bg-[#051C50]/50 border border-[#6693F1]/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6693F1]"
                    placeholder="Subject"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows="5"
                    className="w-full bg-[#051C50]/50 border border-[#6693F1]/30 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6693F1]"
                    placeholder="Your message"
                  />
                </div>

                <button
                  type="submit"
                  ref={(el) => (magneticRefs.current[3] = el)}
                  className="relative overflow-hidden w-full px-8 py-4 rounded-full bg-[#6693F1] text-[#051C50] font-bold group"
                  onMouseEnter={() => setHoveredItem({ color: "#051C50" })}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <span className="relative z-10">Send Message</span>
                  <span className="absolute inset-0 bg-[#F7FEFF] scale-y-0 group-hover:scale-y-100 origin-bottom transition-transform duration-300 ease-in-out" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-8 border-t border-[#6693F1]/20 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div>
              <div className="text-2xl font-bold mb-6">
                <span className="text-[#6693F1]">TECK</span>ANTS
              </div>
              <p className="opacity-80 mb-6"> Your Business is our business</p>
              <div className="flex space-x-4">
                {[...Array(4)].map((_, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-10 h-10 rounded-full bg-[#051C50] border border-[#6693F1]/30 flex items-center justify-center hover:bg-[#6693F1] hover:text-[#051C50] transition-all"
                    onMouseEnter={() => setHoveredItem({ color: "#6693F1" })}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <span className="sr-only">Social {i}</span>
                    {["𝕏", "FB", "IG", "IN"][i]}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Services</h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <a
                      href="#"
                      className="opacity-80 hover:opacity-100 hover:text-[#6693F1] transition-all"
                      onMouseEnter={() => setHoveredItem(service)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {service.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {["Home", "Work", "Services", "Testimonials", "Contact"].map(
                  (item) => (
                    <li key={item}>
                      <button
                        onClick={() => scrollToSection(item.toLowerCase())}
                        className="opacity-80 hover:opacity-100 hover:text-[#6693F1] transition-all"
                        onMouseEnter={() =>
                          setHoveredItem({ color: "#6693F1" })
                        }
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        {item}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Newsletter</h4>
              <p className="opacity-80 mb-4">
                Subscribe to our newsletter for the latest updates.
              </p>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 bg-[#051C50]/50 border border-[#6693F1]/30 rounded-l-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#6693F1]"
                />
                <button
                  className="bg-[#6693F1] text-[#051C50] px-4 py-3 rounded-r-lg font-bold hover:bg-[#7ba1f5] transition-colors"
                  onMouseEnter={() => setHoveredItem({ color: "#051C50" })}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-[#6693F1]/20 pt-8 text-center opacity-80">
            <p>© {new Date().getFullYear()} Teckants. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
